// Territory detail (`/territories/:id`, WEB_PLAN.md §5.8, Phase 1 = read-only): a port of GroundTeam/Views/Territories/
// TerritoryDetailView.swift. Header with the headline numbers, a map of the boundary and the territory's stores
// (colorized like the main map; select a pin to see its card), an overlap warning when another active territory shares
// ground with it, details (kind, area or ZIP codes, team, notes), the assigned reps with how many of its stores each
// owns, and a sortable table of its stores. Editing and "Assign all stores" come with Phase 2.
//
// Numbers follow iOS: stats and per-rep counts use every store the console has (`records(in:)`); the map and the table
// show the viewer's visible stores in it.

import { ChevronLeft, MapPinOff, Maximize2, Minimize2, SearchX, TriangleAlert } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { TERRITORY_KIND_TITLES, type MapColorize } from '@/contracts/enums';
import type { RecordPayload, TerritoryPayload } from '@/contracts/models';
import { parseWireDate } from '@/contracts/wireDate';
import {
  useActivities,
  useDataItem,
  usePipelines,
  useRecordLookups,
  useRecords,
  useSettings,
  useTeams,
  useTerritories,
  useVisibleRecords,
  type DataItem,
} from '@/data/hooks';
import { formatDayDescription, formatGrouped } from '@/domain/format';
import { isValidCoordinate } from '@/domain/geo';
import { legend, pinColorHex, searchRecords } from '@/domain/records';
import { overlappingTerritories } from '@/domain/territories';
import { byId, compareLocalized, fullName, idKey, lookup } from '@/domain/util';
import {
  DaysSinceVisit,
  MapCanvas,
  MoneyText,
  OwnerAvatar,
  PriorityChip,
  RequireFirstSync,
  ScreenLoading,
  StageChip,
  StoreRow,
  useDocumentTitle,
  useStoreRowLookups,
  type MapPoint,
  type MapPolygon,
} from '@/features/common';
import { ColorByMenu } from '@/features/map/ColorByMenu';
import { MapLegend } from '@/features/map/MapLegend';
import {
  Avatar,
  AvatarStack,
  Badge,
  Card,
  Chip,
  DataTable,
  EmptyState,
  IconButton,
  KeyValueRow,
  PageContainer,
  SearchField,
  SectionCard,
  Stat,
  createColumnHelper,
  normalizeHex,
  useTicker,
  withAlpha,
  type DataTableColumn,
  type SortingState,
} from '@/ui';
import { TerritoryKindIcon } from './TerritoryCard';
import { territoryStoreRows, type TerritoryStoreRow } from './storeTable';
import {
  EMPTY_TERRITORY_STATS,
  assignedReps,
  plural,
  storesInTerritory,
  territoryAreaText,
  territoryName,
  territoryStatsMap,
  usesPolygon,
  winRateText,
} from './territoryModel';

const HOUR = 60 * 60 * 1000;

export default function TerritoryDetailPage() {
  const { id } = useParams();
  const item = useDataItem('territories', id);
  useDocumentTitle(item ? territoryName(item.value) : 'Territory');
  return (
    <PageContainer>
      <RequireFirstSync what="Territory" fallback={<ScreenLoading layout="cards" label="Loading territory…" />}>
        {item ? (
          // Keyed by territory: following an overlap link to another territory must start a fresh map (its first fit
          // frames the new boundary), table search and pin selection.
          <TerritoryDetail key={idKey(item.id)} item={item} />
        ) : (
          <div className="flex flex-col gap-4">
            <BackLink />
            <EmptyState icon={<MapPinOff />} color="#8C919E" title="Territory removed" message="This territory no longer exists." />
          </div>
        )}
      </RequireFirstSync>
    </PageContainer>
  );
}

function BackLink() {
  return (
    <Link
      to="/territories"
      className="-ml-1 inline-flex w-fit items-center gap-0.5 rounded-md px-1 text-sm font-medium text-link hover:underline focus-visible:outline-2 focus-visible:outline-focus"
    >
      <ChevronLeft aria-hidden className="size-4" />
      Territories
    </Link>
  );
}

function TerritoryDetail({ item }: { item: DataItem<TerritoryPayload> }) {
  const t = item.value;
  const settings = useSettings();
  const records = useRecords();
  const visible = useVisibleRecords();
  const activities = useActivities();
  const pipelines = usePipelines();
  const territories = useTerritories();
  const teams = useTeams();
  const lk = useRecordLookups();
  const hour = useTicker(HOUR);

  // Every store the console has in it (stats, per-rep counts), and the viewer's visible ones (map, table).
  const members = useMemo(() => storesInTerritory(t, records.values), [t, records.values]);
  const shown = useMemo(() => storesInTerritory(t, visible.values), [t, visible.values]);
  const stats = useMemo(() => {
    void hour;
    const map = territoryStatsMap(
      [t],
      { records: records.values, activities: activities.values, pipelines: pipelines.values, members: new Map([[idKey(t.id), members]]) },
      new Date(),
    );
    return map.get(idKey(t.id)) ?? EMPTY_TERRITORY_STATS;
  }, [t, records.values, activities.values, pipelines.values, members, hour]);
  const reps = useMemo(() => assignedReps(t, lk.users, members), [t, lk.users, members]);
  const overlaps = useMemo(
    () => (t.isActive ? overlappingTerritories(t, territories.values).sort((a, b) => compareLocalized(a.name, b.name)) : []),
    [t, territories.values],
  );
  const teamsById = useMemo(() => byId(teams.values), [teams.values]);
  const team = lookup(teamsById, t.teamId);
  const area = territoryAreaText(t, settings.distanceUnit);
  const win = winRateText(stats);
  const updatedAt = parseWireDate(t.updatedAt);

  return (
    <div className="flex flex-col gap-5 pb-10">
      <BackLink />

      <Card padding="lg" className="gt-hero-soft">
        <div className="flex flex-wrap items-start gap-4">
          <span
            aria-hidden
            className="h-14 w-2 shrink-0 rounded-full"
            style={{ background: `linear-gradient(180deg, ${normalizeHex(t.colorHex)}, color-mix(in srgb, ${normalizeHex(t.colorHex)} 75%, black))` }}
          />
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl leading-tight font-bold tracking-tight text-fg">{territoryName(t)}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <Chip color={t.colorHex} icon={<TerritoryKindIcon kind={t.kind} className="size-3" />}>
                {TERRITORY_KIND_TITLES[t.kind]}
              </Chip>
              {area && <Chip color={t.colorHex}>{area}</Chip>}
              {!t.isActive && <Chip color="#8C919E">Inactive</Chip>}
              {item.undecodable && (
                <Badge tone="warning" title={item.undecodable}>
                  Created by a newer app
                </Badge>
              )}
            </div>
          </div>
          <AvatarStack
            people={reps.map((r) => ({ id: r.user.id, name: fullName(r.user) || 'Unnamed', colorHex: r.user.colorHex }))}
            size={30}
            emptyLabel="No rep assigned"
          />
        </div>
        <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat label={plural(stats.records, 'Store', 'Stores')} value={formatGrouped(stats.records)} />
          <Stat label="Visits · 30d" value={formatGrouped(stats.visits30d)} />
          <Stat label="Won" value={formatGrouped(stats.won)} />
          <Stat label="Pipeline" value={<MoneyText value={stats.pipelineValue} compact />} />
        </div>
        {win && <p className="mt-3 text-xs font-medium text-fg-muted">{win}</p>}
      </Card>

      {overlaps.length > 0 && (
        <div role="note" className="flex items-start gap-3 rounded-card border border-warning/50 bg-warning-soft p-4 text-sm text-fg">
          <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-warning-text" />
          <div className="min-w-0">
            <p className="font-semibold">
              Overlaps {overlaps.length === 1 ? 'another territory' : `${overlaps.length} other territories`}
            </p>
            <p className="mt-0.5 text-fg-muted">
              A store inside more than one active territory belongs to the one created first.{' '}
              {overlaps.map((o, i) => (
                <span key={o.id}>
                  {i > 0 && ', '}
                  <Link to={`/territories/${o.id}`} className="font-medium text-link hover:underline">
                    {territoryName(o)}
                  </Link>
                </span>
              ))}
            </p>
          </div>
        </div>
      )}

      <TerritoryMap territory={t} stores={shown} defaultMode={settings.defaultMapColorize} />

      <div className="grid gap-5 lg:grid-cols-2">
        <SectionCard title="Details">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-fg">
                <TerritoryKindIcon kind={t.kind} className="size-4 text-fg-muted" />
                {TERRITORY_KIND_TITLES[t.kind]}
              </span>
              <span className="flex-1" />
              {!t.isActive && <Chip color="#8C919E">Inactive</Chip>}
              {updatedAt && (
                <Chip color="#8C919E" title={`Updated ${updatedAt.toLocaleString()}`}>
                  {formatDayDescription(updatedAt)}
                </Chip>
              )}
            </div>
            {usesPolygon(t) ? (
              <>
                <KeyValueRow label="Area" value={t.polygon.length >= 3 ? area ?? '—' : 'No boundary drawn'} />
                <KeyValueRow label="Corners" value={formatGrouped(t.polygon.length)} />
              </>
            ) : (
              <div className="flex flex-col gap-1.5">
                <p className="text-sm text-fg-muted">Postal codes</p>
                {t.postalCodes.length === 0 ? (
                  <p className="text-sm text-fg-subtle">None</p>
                ) : (
                  <ul className="flex flex-wrap gap-1.5" aria-label="Postal codes">
                    {t.postalCodes.map((code, i) => (
                      <li key={`${i}:${code}`}>
                        <Chip color={t.colorHex}>{code}</Chip>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {team && <KeyValueRow label="Team" value={team.name} />}
            {t.notes.trim() !== '' && (
              <div className="flex flex-col gap-1 border-t border-line pt-3">
                <p className="text-sm text-fg-muted">Notes</p>
                <p className="text-sm whitespace-pre-wrap text-fg">{t.notes}</p>
              </div>
            )}
          </div>
        </SectionCard>

        <SectionCard title="Assigned reps">
          {reps.length === 0 ? (
            <p className="text-sm text-fg-muted">Nobody is assigned to this territory.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-line">
              {reps.map(({ user, storeCount }) => (
                <li key={user.id} className="flex items-center gap-3 py-2">
                  <Avatar name={fullName(user)} colorHex={user.colorHex} size={28} label={null} />
                  <Link to={`/team/${user.id}`} className="min-w-0 flex-1 truncate text-sm font-medium text-fg hover:underline">
                    {fullName(user) || 'Unnamed'}
                    {!user.isActive && <span className="text-fg-muted"> · Inactive</span>}
                  </Link>
                  <span className="text-xs text-fg-muted tabular-nums">
                    {formatGrouped(storeCount)} {plural(storeCount, 'store', 'stores')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>

      <StoresSection territory={t} stores={shown} />
    </div>
  );
}

// MARK: - Map

function TerritoryMap({
  territory: t,
  stores,
  defaultMode,
}: {
  territory: TerritoryPayload;
  stores: readonly RecordPayload[];
  defaultMode: MapColorize;
}) {
  const lk = useRecordLookups();
  const rowLookups = useStoreRowLookups();
  const visible = useVisibleRecords();
  // iOS `colorizeOverride ?? settings.defaultMapColorize`: the company default (and its later changes) until a pick here.
  const [override, setMode] = useState<MapColorize | null>(null);
  const mode = override ?? defaultMode;
  const [expanded, setExpanded] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const located = useMemo(() => stores.filter((r) => isValidCoordinate(r.coordinate)), [stores]);
  // iOS draws the priority letter on every pin of the territory map.
  const points = useMemo<MapPoint[]>(
    () => located.map((r) => ({ id: r.id, coordinate: r.coordinate, color: pinColorHex(r, mode, lk), label: r.priority })),
    [located, mode, lk],
  );
  const entries = useMemo(() => legend(mode, located, lk), [mode, located, lk]);
  const polygons = useMemo<MapPolygon[]>(
    () => (t.polygon.length >= 3 ? [{ id: t.id, name: territoryName(t), color: t.colorHex, points: t.polygon }] : []),
    [t],
  );
  const selected = selectedId ? visible.get(selectedId) : undefined;

  return (
    <section aria-labelledby="territory-map-title" className="overflow-hidden rounded-card border border-line bg-surface shadow-card">
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">
        <h2 id="territory-map-title" className="mr-auto text-sm font-semibold text-fg">
          Map
          <span className="ml-2 font-normal text-fg-muted">
            {formatGrouped(located.length)} {plural(located.length, 'store', 'stores')} with a location
          </span>
        </h2>
        <ColorByMenu value={mode} onChange={setMode} defaultMode={defaultMode} />
        <IconButton
          label={expanded ? 'Shrink map' : 'Expand map'}
          icon={expanded ? <Minimize2 /> : <Maximize2 />}
          size="sm"
          variant="secondary"
          onClick={() => setExpanded((v) => !v)}
        />
      </div>
      <div className="relative transition-[height] duration-200" style={{ height: expanded ? 520 : 320 }}>
        <MapCanvas
          ariaLabel={`Map of ${territoryName(t)} with ${formatGrouped(points.length)} ${plural(points.length, 'store', 'stores')}`}
          points={points}
          polygons={polygons}
          selectedPointId={selectedId}
          onPointClick={(id) => setSelectedId((cur) => (cur && cur.toLowerCase() === id.toLowerCase() ? null : id))}
        >
          {usesPolygon(t) && t.polygon.length < 3 && (
            <div className="pointer-events-none absolute inset-x-0 top-3 z-10 flex justify-center">
              <span className="rounded-full border border-line bg-surface/95 px-3 py-1.5 text-xs font-medium text-fg-muted shadow-raised">
                No boundary drawn
              </span>
            </div>
          )}
          <div className="pointer-events-none absolute bottom-6 left-3 z-10">
            <MapLegend
              mode={mode}
              entries={entries}
              defaultExpanded={false}
              emptyText="No stores with a location"
              footer={
                polygons.length > 0 ? (
                  <span className="flex items-center gap-2 text-xs text-fg">
                    <span
                      aria-hidden
                      className="h-3 w-4 rounded-[3px] border-[1.5px]"
                      style={{ borderColor: normalizeHex(t.colorHex), backgroundColor: withAlpha(t.colorHex, 0.4) }}
                    />
                    Territory boundary
                  </span>
                ) : undefined
              }
            />
          </div>
        </MapCanvas>
      </div>
      {selected && (
        <div className="border-t border-line p-3">
          <StoreRow item={selected} lookups={rowLookups} variant="compact" showStage href={`/stores/${selected.id}`} />
        </div>
      )}
    </section>
  );
}

// MARK: - Stores table

const col = createColumnHelper<TerritoryStoreRow>();

function StoresSection({ territory: t, stores }: { territory: TerritoryPayload; stores: readonly RecordPayload[] }) {
  const lk = useRecordLookups();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [sorting, setSorting] = useState<SortingState>([{ id: 'name', desc: false }]);

  const rows = useMemo(() => territoryStoreRows(searchRecords(stores, query), lk), [stores, query, lk]);
  const columns = useMemo<DataTableColumn<TerritoryStoreRow>[]>(
    () => [
      col.accessor('name', {
        header: 'Name',
        size: 240,
        meta: { grow: 2 },
        sortingFn: (a, b) => compareLocalized(a.original.name, b.original.name),
        cell: (c) => (
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-fg">{c.row.original.name}</span>
            {c.row.original.subtitle !== '' && <span className="block truncate text-xs text-fg-muted">{c.row.original.subtitle}</span>}
          </span>
        ),
      }),
      col.accessor('daysSort', {
        id: 'daysSinceVisit',
        header: 'Days since visit',
        size: 150,
        sortDescFirst: true,
        cell: (c) => <DaysSinceVisit lastVisitAt={c.row.original.lastVisitAt} now={lk.now} format="short" />,
      }),
      col.accessor('priorityScore', {
        id: 'priority',
        header: 'Priority',
        size: 100,
        sortDescFirst: true,
        cell: (c) => <PriorityChip priority={c.row.original.priority} />,
      }),
      col.accessor('value', {
        header: 'Value',
        size: 120,
        sortDescFirst: true,
        meta: { align: 'right' },
        cell: (c) => <MoneyText value={c.getValue()} zero="—" />,
      }),
      col.accessor('stageOrder', {
        id: 'stage',
        header: 'Stage',
        size: 160,
        meta: { grow: 1 },
        cell: (c) => <StageChip stage={c.row.original.stage} emptyLabel="No stage" />,
      }),
      col.accessor('ownerName', {
        id: 'owner',
        header: 'Owner',
        size: 180,
        meta: { grow: 1 },
        sortingFn: (a, b) => compareLocalized(a.original.ownerName, b.original.ownerName),
        cell: (c) => <OwnerAvatar user={c.row.original.owner} showName />,
      }),
    ],
    [lk.now],
  );

  const sortTitle = sortLabel(sorting);
  const height = rows.length === 0 ? 280 : Math.min(600, 42 + rows.length * 56);

  return (
    <section aria-labelledby="territory-stores-title" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="mr-auto">
          <h2 id="territory-stores-title" className="text-lg font-semibold text-fg">
            Stores
          </h2>
          <p className="text-xs text-fg-muted" aria-live="polite">
            {formatGrouped(rows.length)} {plural(rows.length, 'result', 'results')}
            {sortTitle && ` · Sorted by ${sortTitle}`}
          </p>
        </div>
        <SearchField value={query} onValueChange={setQuery} label="Search stores in territory" size="sm" className="w-full max-w-xs" />
      </div>
      <Card padding="none" className="overflow-hidden">
        <DataTable
          data={rows}
          columns={columns}
          getRowId={(r) => r.id}
          label={`Stores in ${territoryName(t)}`}
          sorting={sorting}
          onSortingChange={setSorting}
          rowHeight={56}
          height={height}
          onRowClick={(r) => navigate(`/stores/${r.id}`)}
          emptyState={
            query.trim() !== '' ? (
              <EmptyState compact icon={<SearchX />} title="No matches" message={`No store in this territory matches "${query.trim()}".`} />
            ) : (
              <EmptyState
                compact
                icon={<MapPinOff />}
                color="#8C919E"
                title="No stores in this territory"
                message={
                  usesPolygon(t)
                    ? 'Stores with a location inside the boundary show up here automatically.'
                    : 'Stores whose address matches one of the postal codes show up here automatically.'
                }
              />
            )
          }
        />
      </Card>
    </section>
  );
}

const SORT_TITLES: Record<string, string> = {
  name: 'Name',
  daysSinceVisit: 'Days since last visit',
  priority: 'Priority',
  value: 'Value',
  stage: 'Stage',
  owner: 'Owner',
};

function sortLabel(sorting: SortingState): string | undefined {
  const first = sorting[0];
  return first ? SORT_TITLES[first.id] : undefined;
}

